import { Button, ButtonGroup, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react'
import { read, WorkBook, utils, Range } from "xlsx";
import util from '../../helpers/util.helper';
import { TextContainerProps } from '../../types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DownloadFileContainer from './DownloadFileContainer';

interface ExcelTableProps {
    headers: string[],
    index: number[],
    htmlBody: string,
    error?: string
}

export default function ExcelContainer({ file, filename, fileExtension, onError }: Readonly<TextContainerProps>) {
    const [workbook, setWorbook] = useState<WorkBook>();
    const [error, setError] = useState('');
    const [sheetName, setSheetName] = useState('');
    const layout = useMemo<ExcelTableProps | null>(() => {
        if (workbook && sheetName && workbook.Sheets[sheetName]?.['!ref']) {
            let range = utils.decode_range(workbook.Sheets[sheetName]?.['!ref'] || '');
            return dataToHtml(utils.sheet_to_html(workbook.Sheets[sheetName]), range);
        }
        return null
    }, [workbook, sheetName])
    const numberSheets = useMemo<number>(() => {
        if (workbook) {
            return Object.keys(workbook.Sheets).length;
        }
        return 0;
    }, [workbook])

    useEffect(() => {
        try {
            if (file) {
                let reader = new FileReader();
                reader.addEventListener("loadend", (e) => {
                    try {
                        let workbook = read(e.target?.result, { type: 'binary' })
                        setWorbook(workbook)
                        setSheetName(Object.keys(workbook.Sheets)[0])
                    } catch (e) {
                        setError(util.validateError(e))
                    }
                })
                reader.readAsArrayBuffer(file)
            }
        } catch (e) {
            setError(util.validateError(e))
        }
    }, [file])

    useEffect(() => {
        if (layout?.error) {
            setError(layout.error)
        }
    }, [layout])

    useEffect(() => {
        if (error) {
            onError?.(error)
        }
    }, [error, onError])

    return (
        <div>
            {!error && <div className='d-flex' style={{ margin: '1rem 0' }}>
                <Typography sx={{ margin: 'auto', fontWeight: 'bolder', width: '100%' }} variant='body2' component={'div'}>
                    {filename}<br/>
                    <Typography variant='caption' color='brown' textAlign={'center'}>
                        *La vista preliminar es una herramienta auxiliar del archivo. Para el uso pleno de este, favor de descargar.
                    </Typography>
                </Typography>
                <Button
                    onClick={() => util.downloadFile(filename, file)}
                    style={{ marginLeft: 'auto' }}
                    variant='outlined'
                    color='success'
                    startIcon={<FontAwesomeIcon icon="download" />}
                    fullWidth
                >
                    Descargar
                </Button>
            </div>}
            {workbook && layout && !error && <div className='excel'>
                <div className='excel-data'>
                    <table>
                        <thead>
                            <tr>
                                <th>#</th>
                                {layout.headers.map(c => <th key={c}>
                                    <div className='resize'>{c}</div>
                                </th>)}
                            </tr>
                        </thead>
                        <tbody dangerouslySetInnerHTML={{ __html: layout.htmlBody }}>
                        </tbody>
                    </table>
                </div>
                <div className='excel-sheets'>
                    <ButtonGroup size='small' color='success'>
                        {Object.keys(workbook?.Sheets).map(v => <Button
                            className={`excel-sheets-item`}
                            key={v}
                            onClick={() => {
                                if (v !== sheetName) {
                                    setSheetName(v)
                                }
                            }}
                            variant={v === sheetName ? 'contained' : 'outlined'}
                            title={v}
                            sx={{ width: numberSheets < 3 ? `fit-content` : '' }}
                        >
                            <small>{numberSheets > 3 ? util.reduceString(v, 18) : v}</small>
                        </Button>)}
                    </ButtonGroup>
                </div>

            </div>}
            {error && file && <DownloadFileContainer file={file} artifact={{ id: 0, name: filename, type: fileExtension || 'xslx' }} />}
        </div>
    )
}

function numberToExcelHeader(index: number) {
    const maxLetter = 26
    let codeLetter: number[] = [];
    if (index > maxLetter) {
        codeLetter = [...numberToExcelHeader(Math.trunc((index / maxLetter))), ...numberToExcelHeader(index % maxLetter)]
    } else {
        codeLetter = [index]
    }
    return codeLetter;
}

function dataToHtml(html: string, range: Range): ExcelTableProps {
    try {
        const padRows = (length: number) => new Array(length).fill(1).map(() => `<td></td>`).join('')
        const div = document.createElement('div')
        const indexMaxValue = range.e.r + 10
        let tableData = ''
        let columnsComplete = 25 - range.e.c;
        columnsComplete = range.e.c + (columnsComplete > 0 ? columnsComplete : 0);
        let padCells = columnsComplete - range.s.c - range.e.c;
        div.innerHTML = html;
        let rows = [...div.querySelectorAll('tr')];
        for (let i = 0; i < indexMaxValue; i++) {
            let placeHolder = `<tr><td>${i + 1}</td>${padRows(columnsComplete + 1)}</tr>`;
            if (range.s.r > i || i > range.e.r) {
                tableData += placeHolder;
            } else {
                let row = rows[i - range.s.r];
                tableData += `<tr>
                <td>${i + 1}</td>
                ${padRows(range.s.c)}
                ${row?.innerHTML}
                ${padRows(padCells + range.s.c)}
            </tr>`;
            }
        }
        div.remove()
        return {
            headers: new Array(columnsComplete + 1).fill(1).map((_, idx) => numberToExcelHeader(idx + 1).map(v => String.fromCharCode(v + 64)).join('')),
            index: new Array(indexMaxValue).fill(1).map((_, idx) => idx + 1),
            htmlBody: tableData
        }
    } catch (e) {
        return { headers: [], index: [], htmlBody: '', error: util.validateError(e) }
    }
}